// eslint-disable-next-line jira/filename-case
import React, { forwardRef, type Ref } from 'react';
import type { RouterLinkComponentProps } from '@atlaskit/app-provider/router-link-provider';
import { fg } from '@atlassian/jira-feature-gating';
import { Link, type LinkProps } from '@atlassian/react-resource-router';
import { useIsEmbedMode } from '@jira/issue__issue-view-embed-mode/src/useIsEmbedMode.tsx';

export type JiraLinkConfig = Pick<
	LinkProps,
	'to' | 'href' | 'replace' | 'params' | 'query' | 'prefetch'
>;

/**
 * Configuration for how `react-resource-router` link components are consumed by Atlassian Design System
 * components through the AppProvider. This maps a supported ADS component `href` prop to the `react-resource-router`
 * `Link` component.
 *
 * In simple cases where the `href` prop is a string, this passes straight through to the `Link` component,
 * and allows ADS components to automatically utilize router links without additional configuration or
 * component overrides.
 * @example
 * ```
 * import { LinkButton } from '@atlaskit/button/new';
 *
 * const MyComponent = () => (
 *   <LinkButton href='my-route'>
 *     Hello world
 *   </LinkButton>
 * );
 * ```
 *
 * For more complex cases, where the `href` prop is an object, this maps the object properties to the `Link` component.
 * To support this, ADS link components support generic types which need to be passed a matching configuration object.
 * In this case, the `JiraLinkConfig` type above is required to be passed.
 * @example
 * ```
 * import type { LinkConfig } from '@atlassian/react-resource-router';
 * import { LinkButton } from '@atlaskit/button/new';
 *
 * const MyComponent = () => (
 *   <LinkButton<LinkConfig>
 *     href={{
 *       to: 'my-route',
 *       prefetch: 'hover',
 *       replace: true,
 *     }}
 *   >
 *     Hello world
 *   </LinkButton>
 * );
 * ```
 *
 * @note ADS has built-in detection of internal, external, and non-HTTP-based links, so this configuration is only used
 * for internal links that need to be handled by the router. Other links will be passed directly to a native `<a>` tag.
 *
 * @see https://atlassian.design/components/app-provider/examples#router-links
 */
export const JiraRouterLinkConfiguration: React.ForwardRefExoticComponent<
	RouterLinkComponentProps<JiraLinkConfig> & React.RefAttributes<HTMLAnchorElement>
> = forwardRef(
	(
		{ href, children, ...rest }: RouterLinkComponentProps<JiraLinkConfig>,
		ref: Ref<HTMLAnchorElement>,
	) => {
		const isEmbedMode = useIsEmbedMode();
		if (
			// Links can be initially rendered with `href` not defined in some cases
			!href ||
			typeof href === 'string'
		) {
			return (
				<Link
					ref={ref}
					href={fg('jira-remove-default-character-for-undefined-hrefs') ? href : href || '#'}
					{...rest}
					{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
				>
					{children}
				</Link>
			);
		}

		return (
			<Link
				ref={ref}
				href={href.href}
				to={href.to}
				replace={href.replace}
				params={href.params}
				query={href.query}
				prefetch={href.prefetch}
				{...rest}
				{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
			>
				{children}
			</Link>
		);
	},
);
